// Reads the RAG matches Brahma reported for a ticket off the event logs it already sent.

import type { SentinelAiEventLogWireDto, SimilarTicketMatch } from '@/modules/sentinel-workspace/types';

/** Brahma ships the map as Dict[str, str], so the array can still arrive JSON-encoded. */
function matchEntries(value: unknown): Record<string, unknown>[] {
  const parsed: unknown = typeof value === 'string' ? safeParse(value) : value;
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object' && !Array.isArray(entry));
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function similarityOf(entry: Record<string, unknown>): number | null {
  const value = Number(entry['similarity']);
  return Number.isFinite(value) ? value : null;
}

/**
 * Every past conversation the ticket matched, strongest first.
 *
 * `v3_past_similar_tickets` names id + score; the older `similar_ticket_ids` column carries
 * ids alone, so both are read and a later turn's score wins for the same conversation.
 */
export function similarTicketsFromApi(events: SentinelAiEventLogWireDto[]): SimilarTicketMatch[] {
  const byConversation = new Map<string, number | null>();

  const remember = (conversationId: string, similarity: number | null): void => {
    const id = conversationId.trim();
    if (!id) return;
    const seen = byConversation.get(id);
    if (seen === undefined || (similarity !== null && (seen === null || similarity > seen))) {
      byConversation.set(id, similarity);
    }
  };

  for (const event of events) {
    for (const entry of matchEntries(event.extra_details?.['v3_past_similar_tickets'])) {
      remember(String(entry['ticket_id'] ?? ''), similarityOf(entry));
    }
    for (const id of event.similar_ticket_ids ?? []) remember(String(id ?? ''), null);
  }

  return [...byConversation.entries()]
    .map(([conversationId, similarity]) => ({ conversationId, similarity }))
    .sort((left, right) => (right.similarity ?? -1) - (left.similarity ?? -1));
}
